// @flow
import * as React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={3} smOffset={5} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Organisation
      </Button>
    </Col>
  </Row>
);

const TableHeader = props => (
  <div>
    <Row>
      <Col sm={4} className="table-header" id="name">
        Organisation name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Organisation Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const organisation = props.item;
  return (
    <Row key={organisation.id}>
      <Col sm={4} className="table-entry">
        {organisation.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        <Button
          className="relative-padding"
          bsStyle="link"
          onClick={() => props.removeItem(organisation)}
          aria-label={`Remove ${organisation.name}`}
        >
          Remove
        </Button>
      </Col>
    </Row>
  );
};

type Params = {
  industryId: number
};

type Props = {
  params: Params,
  currentIndustry: Object,
  getCurrentIndustryOrganisations: (cid: number, iat?: boolean) => void,
  addOrganisationToIndustry: (
    id: number,
    oid: ?number,
    du: ?string
  ) => Promise<*>,
  removeOrganisationFromIndustry: (
    id: number,
    oid: number,
    du: string
  ) => Promise<*>
};

type State = {
  newOrgId: ?number,
  newOrgDateUpdated: ?any,
  showNewOrg: boolean,
  filterValue: string,
  isSaving?: boolean,
  errorMsg?: ?string,
  successMsg?: ?string
};

class IndustryOrganisations extends React.Component<Props, State> {
  fuzzyFilterFactory: any;
  timeoutId: ?any;
  screenReaderContent: any;
  static displayName = 'IndustryOrganisations';

  constructor(props: Props) {
    super(props);
    this.state = {
      newOrgId: undefined,
      newOrgDateUpdated: undefined,
      showNewOrg: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    // get allocated and unallocated organisations
    const currentIndustryId = parseInt(this.props.params.industryId, 10);
    this.props.getCurrentIndustryOrganisations(currentIndustryId, true);
    this.props.getCurrentIndustryOrganisations(currentIndustryId, false);

    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  addOrganisation = () => {
    if (this.state.newOrgId) {
      const industryId = parseInt(this.props.params.industryId, 10);
      const newOrgId = this.state.newOrgId;
      const newOrgDateUpdated = this.state.newOrgDateUpdated;
      this.props
        .addOrganisationToIndustry(industryId, newOrgId, newOrgDateUpdated)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({
      showNewOrg: false,
      newOrgId: undefined,
      newOrgDateUpdated: undefined
    });
  };

  removeOrganisation = (organisation: Object) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const industryId = parseInt(this.props.params.industryId, 10);
    this.props
      .removeOrganisationFromIndustry(
        industryId,
        organisation.id,
        organisation.dateUpdated
      )
      .then(response => this.success(response), error => this.failure(error));
  };

  success = (response: any) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = (filterValue: string) => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewOrg: !this.state.showNewOrg });
  };

  cancel = () => {
    this.setState({ showNewOrg: false, newOrgId: undefined });
  };

  selectOnChange = (newOrgId: number) => {
    const { unallocated } = this.props.currentIndustry.organisations;
    const newOrg = unallocated.find(o => o.id === newOrgId);
    const newOrgDateUpdated = newOrg.dateUpdated;
    this.setState({ newOrgId, newOrgDateUpdated });
  };

  render() {
    const { unallocated, allocated } = this.props.currentIndustry.organisations;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeOrganisation} />
    );

    return (
      <div>
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing organisations with access to industry{' '}
          {this.props.currentIndustry.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewOrg && (
          <AddNewRow
            items={unallocated}
            addItem={this.addOrganisation}
            selectValue={this.state.newOrgId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="organisations"
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default IndustryOrganisations;



// WEBPACK FOOTER //
// ./src/containers/industries/IndustryOrganisations.js